import type { Music24Capabilities } from '../music24Plans';
import type { OutreachCampaignGoal } from './bestFits';
import type { OutreachTargetKind } from './types';

/**
 * The daily pitch rules of Outreach v2 (plan 2026-10-23). Plug sends a fixed number of pitches each
 * day for one artist (`outreachPitchesPerDay` of the plan). Two running campaigns of one artist
 * share that number. A campaign ends some weeks after the release day. Pure: no I/O.
 */

/**
 * The kinds that an artist picks for a campaign. A playlist target is a Spotify playlist or an
 * other playlist; every other send kind keeps its target kind.
 */
export type OutreachPitchKind =
  | 'spotifyPlaylist'
  | 'otherPlaylist'
  | 'press'
  | 'newsletter'
  | 'radio'
  | 'podcast'
  | 'creator';

/** The value list, for zod enums. Same order as the type. */
export const OUTREACH_PITCH_KINDS: readonly OutreachPitchKind[] = [
  'spotifyPlaylist',
  'otherPlaylist',
  'press',
  'newsletter',
  'radio',
  'podcast',
  'creator',
];

/** True for a value of `OUTREACH_PITCH_KINDS` (the `kinds` column is a plain text array). */
export function isOutreachPitchKind(value: string): value is OutreachPitchKind {
  return (OUTREACH_PITCH_KINDS as readonly string[]).includes(value);
}

/**
 * The pitch kinds of a new campaign from the goal of the Song step. `playlists`: both playlist
 * kinds. `press`: press and newsletters. `radio`: radio and podcasts. `mixed`: every kind but
 * radio and podcasts. The artist can change the kinds later (`campaigns.update`).
 */
export function pitchKindsOfGoal(goal: OutreachCampaignGoal): OutreachPitchKind[] {
  switch (goal) {
    case 'playlists':
      return ['spotifyPlaylist', 'otherPlaylist'];
    case 'press':
      return ['press', 'newsletter'];
    case 'radio':
      return ['radio', 'podcast'];
    case 'mixed':
      return OUTREACH_PITCH_KINDS.filter((kind) => kind !== 'radio' && kind !== 'podcast');
  }
}

/** The weeks that a campaign runs after the release day when the artist does not choose. */
export const OUTREACH_CAMPAIGN_DEFAULT_WEEKS = 8;

const DAY_MS = 24 * 60 * 60 * 1000;

/**
 * The pitch kind of one target. A `community` target is a reference entry with no email, so it has
 * no pitch kind: null.
 */
export function pitchKindOfTarget(target: {
  kind: OutreachTargetKind;
  service: string | null;
}): OutreachPitchKind | null {
  switch (target.kind) {
    case 'playlist':
      return target.service === 'spotify' ? 'spotifyPlaylist' : 'otherPlaylist';
    case 'press':
    case 'newsletter':
    case 'radio':
    case 'podcast':
    case 'creator':
      return target.kind;
    case 'community':
      return null;
  }
}

/**
 * The pitches a day of one artist on this plan. On a team plan (Manager, Label) each artist gets
 * this number; `music24OutreachPoolLimit` gives the number of the whole workspace.
 */
export function outreachPitchesPerDay(capabilities: Music24Capabilities): number {
  return Math.max(0, Math.floor(capabilities.outreachPitchesPerDay));
}

/** The day number (days since 1970-01-01, UTC) of a `YYYY-MM-DD` string. */
function dayNumber(day: string): number {
  return Math.floor(Date.parse(`${day.slice(0, 10)}T00:00:00Z`) / DAY_MS);
}

/**
 * Splits the pitches of one day between the running campaigns of one artist. A campaign with a
 * `dailyShare` gets its share first (nearer release day first, never more than is left). The rest
 * goes one by one to the campaigns without a share, nearer release day first, so the song with the
 * nearer release day gets the larger part. When every campaign has a share, the rest goes to all
 * of them in the same order. A campaign with no release day comes last. The sum is `total`.
 */
export function splitDailyPitches(
  total: number,
  campaigns: { id: number; releaseDate: string | null; dailyShare: number | null }[],
  today: string
): Map<number, number> {
  const result = new Map<number, number>(campaigns.map((campaign) => [campaign.id, 0]));
  if (campaigns.length === 0) {
    return result;
  }
  const todayNumber = dayNumber(today);
  const distance = (releaseDate: string | null): number =>
    releaseDate === null ? Number.POSITIVE_INFINITY : Math.abs(dayNumber(releaseDate) - todayNumber);
  const ordered = [...campaigns].sort((a, b) => {
    // Two songs with no release day give NaN (Infinity - Infinity): the id decides then.
    const byDistance = distance(a.releaseDate) - distance(b.releaseDate);
    return byDistance !== 0 && !Number.isNaN(byDistance) ? byDistance : a.id - b.id;
  });

  let left = Math.max(0, Math.floor(total));
  for (const campaign of ordered) {
    if (campaign.dailyShare === null) {
      continue;
    }
    const share = Math.min(left, Math.max(0, Math.floor(campaign.dailyShare)));
    result.set(campaign.id, share);
    left -= share;
  }

  const withoutShare = ordered.filter((campaign) => campaign.dailyShare === null);
  const receivers = withoutShare.length > 0 ? withoutShare : ordered;
  for (let index = 0; left > 0; index += 1, left -= 1) {
    const receiver = receivers[index % receivers.length];
    if (receiver === undefined) {
      break;
    }
    result.set(receiver.id, (result.get(receiver.id) ?? 0) + 1);
  }
  return result;
}

/**
 * The end of a campaign: `weeks` after the release day. A song whose release day is before the
 * start day (an old song), or a song with no release day, counts from the start day. Null weeks
 * means "until I stop it": null. The answer is midnight UTC of the end day.
 */
export function campaignEndsAt(input: {
  releaseDate: string | null;
  startedAt: Date;
  weeks: 4 | 8 | 12 | null;
}): Date | null {
  if (input.weeks === null) {
    return null;
  }
  const startDay = Math.floor(input.startedAt.getTime() / DAY_MS);
  const releaseDay = input.releaseDate === null ? null : dayNumber(input.releaseDate);
  const fromDay = releaseDay !== null && releaseDay >= startDay ? releaseDay : startDay;
  return new Date((fromDay + input.weeks * 7) * DAY_MS);
}
